import React from 'react';
import { MapPin, ExternalLink, Navigation, Compass } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const branchCoordinates = {
  sangli: {
    lat: 16.847833,
    lng: 74.571443,
    googleEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3818.572704085449!2d74.5714428751522!3d16.84783308395066!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc11866ea66371b%3A0xe24f4996fb75ed20!2sSree%20Tekke%20Eye%20Clinic!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
  },
  astha: {
    lat: 16.9472,
    lng: 74.4074
  },
  jath: {
    lat: 17.0478,
    lng: 75.2144
  },
  atpadi: {
    lat: 17.4206,
    lng: 74.9453
  },
  solapur: {
    lat: 17.6599,
    lng: 75.9064
  },
  athani: {
    lat: 16.7327,
    lng: 75.0601
  },
  sangola: {
    lat: 17.4339,
    lng: 75.1978
  }
};

const MapSection = ({ branch, height = "h-full min-h-[420px] lg:min-h-[500px]" }) => {
  const { t, language } = useLanguage();

  const slug = branch?.slug || 'sangli';
  const coords = branchCoordinates[slug] || branchCoordinates.sangli;
  const isSangli = slug === 'sangli';

  const branchName = branch?.name || t('contactSection.mainClinicTitle');
  const branchAddress = branch?.address || t('contactSection.mainClinicLocation');
  const mapUrl = branch?.mapUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(branchName + ' ' + branchAddress)}`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(branchName + ' ' + branchAddress)}`;

  const embedSrc = isSangli && coords.googleEmbed
    ? coords.googleEmbed
    : `https://www.openstreetmap.org/export/embed.html?bbox=${coords.lng - 0.022}%2C${coords.lat - 0.016}%2C${coords.lng + 0.022}%2C${coords.lat + 0.016}&layer=mapnik&marker=${coords.lat}%2C${coords.lng}`;

  return (
    <div className={`w-full ${height} bg-slate-100 rounded-2xl overflow-hidden relative shadow-sm border border-slate-200/90 flex flex-col group`}>
      <iframe
        key={slug}
        title={`${branchName} Map Location`}
        src={embedSrc}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        className="w-full h-full flex-1 transition-opacity duration-300"
      ></iframe>

      {/* Floating Info & Direct Google Maps Link (Top-Left) */}
      <div className="absolute top-3.5 left-3.5 z-10 bg-white/95 backdrop-blur-md p-3.5 sm:p-4 rounded-2xl shadow-xl border border-white/80 max-w-[280px] sm:max-w-xs transition-all duration-300">
        <div className="flex items-start gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-clinical/10 text-clinical flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
            <MapPin size={17} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-teal">
                {language === 'mr' ? 'थेट नेव्हिगेशन' : 'Live GPS Pin'}
              </span>
            </div>
            <h4 className="font-extrabold text-navy text-xs sm:text-sm leading-snug line-clamp-2">
              {branchName}
            </h4>
            <p className="text-gray-500 text-[11px] sm:text-xs mt-1 line-clamp-2 leading-relaxed">
              {branchAddress}
            </p>
            <div className="flex items-center gap-3 mt-2.5 pt-2 border-t border-slate-100">
              <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-clinical hover:text-navy text-xs font-bold transition-colors group/link"
              >
                <span>{t('contactSection.openInMaps')}</span>
                <ExternalLink size={11} className="group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Quick Driving Direction Button (Bottom-Right) */}
      <div className="absolute bottom-3.5 right-3.5 z-10 flex items-center gap-2">
        <a
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-navy/95 hover:bg-navy text-white text-xs font-bold shadow-lg hover:shadow-xl backdrop-blur-md border border-white/10 transition-all hover:scale-[1.02] group/dir"
        >
          <Navigation size={13} className="text-amber-400 group-hover/dir:rotate-45 transition-transform duration-300" />
          <span>{language === 'mr' ? 'गुगल ड्रायव्हिंग मार्ग' : 'Get Driving Directions'}</span>
          <ExternalLink size={11} className="text-teal" />
        </a>
      </div>

      {/* Floating GPS Coordinates Badge (Bottom-Left) */}
      <div className="absolute bottom-3.5 left-3.5 z-10 hidden sm:flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/90 backdrop-blur-md border border-slate-200/80 text-[10px] font-bold text-slate-600 shadow-sm pointer-events-none">
        <Compass size={11} className="text-teal" />
        <span>GPS: {coords.lat.toFixed(4)}° N, {coords.lng.toFixed(4)}° E</span>
      </div>
    </div>
  );
};

export default MapSection;
